import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Prompt, useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskLabelGroupCategory,
  createTaskProjectTemplateSetting,
  fetchTaskLabelSettingNewDetail,
  fetchTaskProjectTemplateSettingDetail,
  updateTaskProjectTemplateSetting,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import {
  RequestPatchTaskProjectTemplateSetting,
  RequestPostTaskLabelGroupCategory,
  RequestPostTaskProjectTemplateSetting,
} from '../../../../states/types/task'
import { replaceAndStrip } from '../../../../utils/string'
import { setPath } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AddColorModule from '../../../components/task/AddColorModule'
import AddTeamAndMemberModule from '../../../components/setting/AddTeamAndMemberModule'
import SetLabelModule from '../../../components/task/SetLabelModule'
import { URL } from '../../../constants/url'
import COLOR_LIST from '../../../constants/taskColor'
import { RoleMember } from '../../../../states/types/setting'
import AddCategoryModule from '../../../components/task/AddCategoryModule'
import AddLabelGroupCategoryModal from '../../../components/task/AddLabelGroupCategoryModal'

interface Team {
  id: number
  scope: string
  name: string
  include_nested_teams: boolean | null
}

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  description?: string
  permission: {
    viewable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
    }
    enable_settable_permission: boolean
    settable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

interface ModalState {
  label_group_id: number | null
  label_group_name: string
  label_id?: number | null
  label_name?: string
  label_code?: string | null
  label_color?: string
  label_master_id?: number | null
  scheduled_time?: number // 想定工数(秒)
  type: string
}

type LocationState = {
  keep?: boolean
  labelGroupId?: number
  detail?: TemplateDetail | null
  from?: string
}

interface Category {
  id: number
  category_name: string
  color: string
  parent_id: number | null
}

interface MemberDetail {
  member_id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface CategoryList {
  id: number
  category_name: string
  color: string
  parent_id: number | null
  members: MemberDetail[]
  parent_members: MemberDetail[]
}

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const Detail: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const {
    task,
  } = useSelector((state: RootState) => state)

  const [inputGroupName, setInputGroupName] = useState<string>("")
  const [inputGroupCode, setInputGroupCode] = useState<string>("")
  const [inputColor, setInputColor] = useState<string>("#f2a2a2")
  const [inputDescription, setInputDescription] = useState<string>("")
  const [selectUseTeams, setSelectUseTeams] = useState<Team[]>([])
  const [selectUseMembers, setSelectUseMembers] = useState<{ id: number, name: string }[]>([])
  const [selectViewTeams, setSelectViewTeams] = useState<Team[]>([])
  const [selectViewMembers, setSelectViewMembers] = useState<{ id: number, name: string }[]>([])
  const [selectSettableTeams, setSelectSettableTeams] = useState<Team[]>([])
  const [selectSettableMembers, setSelectSettableMembers] = useState<{ id: number, name: string }[]>([])
  const [inputLabels, setInputLabels] = useState<ModalState[]>([])
  const [labelsError, setLabelsError] = useState<string>("")
  const [selectCategoryIds, setSelectCategoryIds] = useState<number[]>([])
  const [isSettableMember, setIsSettableMember] = useState<boolean>(false)

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const [isDisplayAddCategory, setIsDisplayAddCategory] = useState<boolean>(false)

  const locationState = history.location.state as LocationState

  const sortCategory = (parentId: number | null): Category[] => {
    if (!task.taskLabelSettingNew?.label_group_categories) return []
    const tmp = task.taskLabelSettingNew?.label_group_categories.filter((v) => v.parent_id === parentId)
    if (!tmp?.length) return []
    return tmp.map((v) => [v, ...sortCategory(v.id)]).flat()
  }

  const formattedCategory = useMemo(() => (
    sortCategory(null)
  ), [task.taskLabelSettingNew?.label_group_categories])

  const modalStateInit = {
    label_group_id: null,
    label_group_name: "",
    label_name: "",
    type: "create",
  }

  const templateDetail = useMemo(() => {
    if (!id && !task) return undefined
    return task.taskProjectTemplateSettingDetail?.label_group_template
  }, [id, task])

  const title = useMemo(() => {
    if (id) {
      return "ユーザーテンプレート編集"
    }
    return "新規ユーザーテンプレート作成"
  }, [id, locationState])

  const description = useMemo(() => {
    if (id) {
      return (
        <>
          <p>
            ユーザーテンプレートを編集することができます。
          </p>
        </>
      )
    }
    return (
      <>
        <p>
          ユーザーテンプレートを作成し、プロジェクト作成時に使用することができます。
        </p>
      </>
    )
  }, [id, locationState])

  const tasks = useMemo(() => (
    [...task.taskLabelSettingNew?.label_masters.filter((v) => !inputLabels.map((w) => w.label_name).includes(v.name)) || []]
  ), [task, inputLabels])

  const categoryViewMembers = useMemo(() => (
    Array.from(new Map(
      (task.taskLabelSettingNew?.label_group_categories.find((v) => selectCategoryIds.includes(v.id))?.viewer || []).map((v) => [v.id, v]),
    ).values())
  ), [task.taskLabelSettingNew?.label_group_categories, selectCategoryIds])

  const getAllMemberList = (teamId: number | null): RoleMember[] => {
    if (!task.taskLabelSettingNew?.teams) return []
    const tmpTeams = task.taskLabelSettingNew.teams.filter((v) => v.parent_team_id === teamId)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const buildCheckBox = (value: boolean) => (
    value ? (
      <FontAwesomeIcon
        icon={["fas", "check-square"]}
        style={{ color: "#008800" }}
        fixedWidth
      />
    ) : (
      <FontAwesomeIcon
        icon={["far", "square"]}
        style={{ color: "#999999" }}
        fixedWidth
      />
    )
  )

  const randomInt = (min: number, max: number) => {
    const intMin = Math.ceil(min)
    const intMax = Math.floor(max)
    return Math.floor(Math.random() * (intMax - intMin) + intMin)
  }

  const randomColor = () => COLOR_LIST.map((v) => v.values).flat()[randomInt(0, 23)].color

  const addLabelGroup = () => {
    setIsSubmit(true)
    setIsEdit(false)
    if (!id) {
      const tmpVal: RequestPostTaskProjectTemplateSetting = {
        name: inputGroupName,
        code: inputGroupCode || null,
        color: inputColor || "",
        description: inputDescription || "",
        permission: {
          viewable: {
            teams: selectViewTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [],
            member_ids: selectViewMembers.map((v) => v.id) || [],
          },
          usable: {
            teams: selectUseTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [],
            member_ids: selectUseMembers.map((v) => v.id) || [],
          },
          enable_settable_permission: isSettableMember,
          settable: {
            // eslint-disable-next-line max-len
            teams: isSettableMember ? selectSettableTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [] : [],
            member_ids: isSettableMember ? selectSettableMembers.map((v) => v.id) || [] : [],
          },
        },
        labels: inputLabels.filter((v) => v.label_name).map((v) => ({
          label_master_id: v.label_master_id || null,
          name: v.label_name || "",
          code: v.label_code || null,
          color: v.label_color || randomColor(),
        })) || [],
        label_group_category_ids: selectCategoryIds,
      }
      dispatch(
        createTaskProjectTemplateSetting(
          tmpVal,
          () => {
            alert.success("ユーザーテンプレートを作成しました！")
            if (locationState.from && locationState?.keep) {
              history.replace(locationState.from, { keep: true })
            } else {
              history.push(`${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}`)
            }
          },
        ),
      )
    } else {
      const tmpVal: RequestPatchTaskProjectTemplateSetting = { id: id }
      if (templateDetail?.name !== inputGroupName) {
        tmpVal.name = inputGroupName
      }
      if (templateDetail?.code !== inputGroupCode) {
        tmpVal.code = inputGroupCode || null
      }
      if (inputColor && templateDetail?.color !== inputColor) {
        tmpVal.color = inputColor
      }
      if (inputColor && templateDetail?.description !== inputDescription) {
        tmpVal.description = inputDescription.replace(/\n\n/g, "\n")
      }
      tmpVal.permission = {
        viewable: {
          teams: selectViewTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [],
          member_ids: selectViewMembers.map((v) => v.id) || [],
        },
        usable: {
          teams: selectUseTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [],
          member_ids: selectUseMembers.map((v) => v.id) || [],
        },
        enable_settable_permission: isSettableMember,
        settable: {
          // eslint-disable-next-line max-len
          teams: isSettableMember ? selectSettableTeams.map((v) => ({ id: v.id, scope: v.scope, include_nested_teams: v.include_nested_teams })) || [] : [],
          member_ids: isSettableMember ? selectSettableMembers.map((v) => v.id) || [] : [],
        },
      }
      tmpVal.labels = inputLabels.filter((v) => v.label_name).map((v) => ({
        label_id: v.label_id || null,
        label_master_id: v.label_master_id || 0,
        name: v.label_name || "",
        code: v.label_code || null,
        color: v.label_color || randomColor(),
      }))
      tmpVal.label_group_category_ids = selectCategoryIds
      dispatch(
        updateTaskProjectTemplateSetting(
          tmpVal,
          () => {
            alert.success("ユーザーテンプレートを編集しました！")
            if (locationState.from && locationState?.keep) {
              history.replace(locationState.from, { keep: true })
            } else {
              history.push(`${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}`)
            }
          },
        ),
      )
    }
  }

  const changeCategoryIds = (categoryIds: number[]) => {
    setIsEdit(true)
    setSelectCategoryIds(categoryIds)
  }

  const changeViewTeam = (teams: Team[]) => {
    setIsEdit(true)
    setSelectViewTeams(teams)
  }

  const changeViewMember = (memberList: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectViewMembers(memberList)
  }

  const selectViewTarget = (data: { teams: Team[], members: { id: number, name: string }[] }) => {
    changeViewTeam(data.teams)
    changeViewMember(data.members)
  }

  const changeUseTeam = (teams: Team[]) => {
    setIsEdit(true)
    setSelectUseTeams(teams)
  }

  const changeUseMember = (memberList: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectUseMembers(memberList)
  }

  const selectUseTarget = (data: { teams: Team[], members: { id: number, name: string }[] }) => {
    changeUseTeam(data.teams)
    changeUseMember(data.members)
  }

  const changeSettableTeam = (teams: Team[]) => {
    setIsEdit(true)
    setSelectSettableTeams(teams)
  }

  const changeSettableMember = (memberList: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectSettableMembers(memberList)
  }

  const selectSettableTarget = (data: { teams: Team[], members: { id: number, name: string }[] }) => {
    changeSettableTeam(data.teams)
    changeSettableMember(data.members)
  }

  const changeInputColor = (value: string) => {
    setIsEdit(true)
    setInputColor(value)
  }

  const changeInputGroupName = (value: string) => {
    setIsEdit(true)
    setInputGroupName(value)
  }

  const changeInputDescription = (value: string) => {
    setIsEdit(true)
    setInputDescription(value)
  }

  const existsSameValue = (arr: string[]) => {
    const tmpArr = new Set(arr)
    return tmpArr.size !== arr.length
  }

  const addLabelList = () => {
    setIsEdit(true)
    setInputLabels([...inputLabels, modalStateInit])
  }

  const delLabelList = (num: number) => {
    setIsEdit(true)
    setInputLabels([...inputLabels.filter((_v, i) => i !== num)])
  }

  const selectLabel = (labelName: string, num: number) => {
    setIsEdit(true)
    const param = tasks.find((v) => v.name === labelName)
    if (!param) {
      setInputLabels(
        [...inputLabels.map((v, i) => (i === num ? {
          ...modalStateInit,
          label_name: labelName,
        } : v))],
      )
      return
    }
    setInputLabels(
      [...inputLabels.map((v, i) => (i === num ? {
        ...modalStateInit,
        label_name: param.name,
        label_master_id: param.id,
        label_color: param.color,
      } : v))],
    )
  }

  const handleBeforeUnloadEvent = (event: BeforeUnloadEvent): void => {
    event.preventDefault()
    event.returnValue = "check"
  }

  const cancel = () => {
    if (locationState.from && locationState?.keep) {
      history.replace(locationState.from, { keep: true })
    } else {
      history.goBack()
    }
  }

  const buildTargetMember = (team: Team, scope: string) => {
    const targetTeam = task.taskLabelSettingNew?.teams.find((v) => v.id === team.id)
    if (!targetTeam) return []
    if (scope === "manager") {
      return targetTeam.managers
    }
    if (scope === "member") {
      return targetTeam.members
    }
    return Array.from(new Set([...targetTeam.managers, ...targetTeam.members]))
  }

  const buildCategoryList = (parent_id?: number): CategoryList[] => {
    let list = []
    if (parent_id) {
      list = formattedCategory.filter((v) => v.parent_id === parent_id)
    } else {
      list = formattedCategory.filter((v) => v.parent_id === null)
    }
    if (!list.length) return []
    const current = list.map((v) => ({ ...v, members: [], parent_members: [] }))
    return Array.from(
      new Set(
        [
          ...current,
          ...list.map((v) => buildCategoryList(
            v.id,
          )).flat(),
        ],
      ),
    )
  }

  // eslint-disable-next-line max-len
  const addProjectCategory = (param: {category_name: string, color: string, parent_id?: number | null, member_ids?: number[], label_group_ids?: number[] }) => {
    const tmpValue: RequestPostTaskLabelGroupCategory = {
      category_name: param.category_name,
      color: param.color,
      parent_id: param.parent_id || null,
      member_ids: param.member_ids || [],
      label_group_ids: param.label_group_ids || [],
      scheduled_start_date: null,
      scheduled_end_date: null,
    }
    dispatch(
      createTaskLabelGroupCategory(
        tmpValue,
        (result) => {
          alert.success(`カテゴリー「${param.category_name}」を追加しました`)
          dispatch(fetchTaskLabelSettingNewDetail(true))
          setSelectCategoryIds((cur) => [...cur, result.id])
        },
      ),
    )
    setIsDisplayAddCategory(false)
  }

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnloadEvent)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnloadEvent)
    }
  }, [])

  useEffect(() => {
    setInputGroupName(templateDetail?.name || "")
    setInputGroupCode(templateDetail?.code || "")
    setInputColor(templateDetail?.color || randomColor())
    setInputDescription(templateDetail?.description || "")
    setSelectCategoryIds(templateDetail?.label_group_category_ids || [])
    setSelectUseTeams(templateDetail?.permission.usable.teams || [])
    setSelectUseMembers(templateDetail?.permission.usable.members || [])
    setSelectViewTeams(templateDetail?.permission.viewable.teams || [])
    setSelectViewMembers(templateDetail?.permission.viewable.members || [])
    setSelectSettableTeams(templateDetail?.permission.settable.teams || [])
    setSelectSettableMembers(templateDetail?.permission.settable.members || [])
    setIsSettableMember(templateDetail?.permission.enable_settable_permission || false)
    setInputLabels(
      templateDetail?.labels.map((v) => (
        {
          label_group_id: templateDetail.id,
          label_group_name: templateDetail.name,
          label_id: v.id,
          label_name: v.name,
          label_code: v.code,
          label_color: v.color,
          label_master_id: v.label_master_id,
          type: "update",
        }
      )) || [],
    )
  }, [templateDetail])

  useEffect(() => {
    if (existsSameValue(inputLabels.filter((v) => v.label_name).map((v) => v.label_name || ""))) {
      setLabelsError("業務が重複しています。異なる業務を設定してください。")
    } else {
      setLabelsError("")
    }
  }, [inputLabels])

  useEffect(() => {
    if (id) {
      dispatch(fetchTaskProjectTemplateSettingDetail({ id: id }))
    }
  }, [id, locationState])

  useEffect(() => {
    window.scrollTo(0, 0)
    document.querySelector('html')?.classList.remove('modal_open')
    dispatch(fetchTaskLabelSettingNewDetail())
    setInputGroupName("")
    setInputGroupCode("")
    setInputColor(randomColor())
    setInputDescription("")
    setSelectCategoryIds([])
    setSelectUseTeams([])
    setSelectUseMembers([])
    setSelectViewTeams([])
    setSelectViewMembers([])
    setSelectSettableTeams([])
    setSelectSettableMembers([])
    setIsSettableMember(false)
    setInputLabels([])
  }, [location])

  useEffect(() => {
    const tmpIds = categoryViewMembers.map((v) => v.id)
    setSelectViewMembers((cur) => cur.filter((v) => !tmpIds.includes(v.id)))
  }, [categoryViewMembers])

  return (
    <section className="task_page task_status">
      <Helmet>
        <title>{`Qasee - 工数管理 - ユーザーテンプレート設定 - ${title}`}</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}
        >
          ユーザーテンプレート設定
        </Link>
        &nbsp;&gt;&nbsp;
        {title}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {title}
        </h1>
      </section>
      <section className="page_description">
        {description}
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    ユーザーテンプレートを読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div className="project_input_area">
                    <fieldset>
                      <legend>
                        ユーザーテンプレート概要
                      </legend>
                      <div className="item w20">
                        <div className="title">
                          プロジェクトカラー
                        </div>
                        <div className="detail equal_space row_1">
                          <AddColorModule
                            color={inputColor}
                            changeCallback={(color) => changeInputColor(color)}
                          />
                        </div>
                      </div>
                      <div className="item w80">
                        <div className="title">
                          <div className="required">必須</div>
                          テンプレート名
                        </div>
                        <div className="detail equal_space row_1">
                          <input
                            type="text"
                            value={inputGroupName}
                            onChange={(e) => changeInputGroupName(e.target.value)}
                            onBlur={() => changeInputGroupName(replaceAndStrip(inputGroupName))}
                            placeholder="プロジェクト名を入力"
                          />
                        </div>
                        {
                          !inputGroupName.length && (
                            <div className="error">
                              テンプレート名を入力してください
                            </div>
                          )
                        }
                      </div>
                      {
                        !!task.taskLabelSettingNew?.label_group_categories.length && (
                          <div className="item w100">
                            <div className="title">
                              プロジェクトカテゴリー
                            </div>
                            <div className="detail equal_space row_1">
                              <div
                                className="team_area"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  gap: "16px",
                                }}
                              >
                                {
                                  !!selectCategoryIds.length && (
                                    <>
                                      <div
                                        className="chip_area"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        {
                                          formattedCategory.filter((v) => selectCategoryIds.includes(v.id)).map((v) => (
                                            <div
                                              className="chip"
                                              key={`category_${v.id}`}
                                              style={{
                                                display: "flex",
                                                padding: "4px 8px",
                                                margin: 0,
                                              }}
                                            >
                                              <FontAwesomeIcon icon={['fas', 'folder']} style={{ color: v.color }} />
                                              {v.category_name}
                                              <div>
                                                <span
                                                  className="delete"
                                                  onClick={() => changeCategoryIds([...selectCategoryIds.filter((w) => w !== v.id)])}
                                                  role="none"
                                                  style={{
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'times']} />
                                                </span>
                                              </div>
                                            </div>
                                          ))
                                        }
                                      </div>
                                    </>
                                  )
                                }
                                <div
                                  className="chip_area"
                                >
                                  <AddCategoryModule
                                    categoryList={formattedCategory}
                                    selectCategoryIdList={selectCategoryIds}
                                    callback={(categoryIds) => changeCategoryIds(categoryIds)}
                                    createCategoryCallback={() => {
                                      setIsDisplayAddCategory(true)
                                    }}
                                    isRealTime
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }
                      <div className="item w100">
                        <div className="title">
                          説明
                        </div>
                        <div className="detail equal_space row_1">
                          <textarea
                            style={{
                              height: "auto",
                              width: "100%",
                              padding: "16px",
                              backgroundColor: "#FFFFFF",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              fontSize: "14px",
                              minHeight: "7em",
                            }}
                            value={inputDescription}
                            onChange={(e) => changeInputDescription(e.target.value)}
                          />
                        </div>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {`プロジェクトレポート表示${isSettableMember ? "" : "・編集権限"}対象`}
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div
                            className="team_area"
                            style={{
                              backgroundColor: "#FFFFFF",
                              gap: "16px",
                            }}
                          >
                            {
                              !!selectViewTeams.length && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "8px",
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    padding: "16px",
                                    position: "relative",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#FFFFFF",
                                      padding: "0 4px",
                                      position: "absolute",
                                      top: 0,
                                      left: "8px",
                                      transform: "translateY(-50%)",
                                      fontSize: "10px",
                                      color: "#666666",
                                    }}
                                  >
                                    チーム
                                  </div>
                                  {
                                    selectViewTeams.map((team) => (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px",
                                            borderBottom: "solid 1px #CCCCCC",
                                            backgroundColor: "#EEEEEE",
                                          }}
                                        >
                                          <div>
                                            {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                          </div>
                                          <div>
                                            <span
                                              className="delete"
                                              onClick={() => changeViewTeam([...selectViewTeams.filter((v) => v.id !== team.id)])}
                                              role="none"
                                              style={{
                                                cursor: "pointer",
                                              }}
                                            >
                                              <FontAwesomeIcon icon={['fas', 'times']} />
                                            </span>
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "12px",
                                            padding: "12px 8px 8px",
                                          }}
                                        >
                                          {
                                            ["both", "manager"].includes(team.scope) && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  マネージャー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    buildTargetMember(team, "manager").map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  このチームのメンバー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    buildTargetMember(team, "member").map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    Array.from(new Map([
                                                      ...getAllMemberList(team.id || null),
                                                      ...buildTargetMember(team, "member"),
                                                    ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                              )
                            }
                            {
                              (!!selectViewMembers.length || !!categoryViewMembers.length) && (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    padding: "16px",
                                    position: "relative",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#FFFFFF",
                                      padding: "0 4px",
                                      position: "absolute",
                                      top: 0,
                                      left: "8px",
                                      transform: "translateY(-50%)",
                                      fontSize: "10px",
                                      color: "#666666",
                                    }}
                                  >
                                    ユーザー
                                  </div>
                                  <div className="chip_area">
                                    {
                                      categoryViewMembers.map((member) => (
                                        <Tips
                                          description="プロジェクトが所属しているカテゴリーの表示ユーザーです。削除することはできません。"
                                          className=""
                                          icon={false}
                                        >
                                          <>
                                            <div
                                              className="chip"
                                              key={`category_manager_${member.id}`}
                                              style={{ margin: 0, position: "relative" }}
                                            >
                                              {member.name}
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  right: "-4px",
                                                  top: "-5px",
                                                  backgroundColor: "#cd2525",
                                                  borderRadius: "50%",
                                                  padding: "2px",
                                                  color: "#FFFFFF",
                                                  fontSize: "8px",
                                                }}
                                              >
                                                <FontAwesomeIcon icon={['fas', 'folder']} />
                                              </div>
                                            </div>
                                          </>
                                        </Tips>
                                      ))
                                    }
                                    {
                                      selectViewMembers.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                          <span
                                            className="delete"
                                            onClick={() => changeViewMember([...selectViewMembers.filter((v) => v.id !== member.id)])}
                                            role="none"
                                          >
                                            <FontAwesomeIcon icon={['fas', 'times']} />
                                          </span>
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              )
                            }
                            <div className="chip_area">
                              <AddTeamAndMemberModule
                                selectMemberIdList={selectViewMembers.map((v) => v.id)}
                                selectTeamList={selectViewTeams}
                                callback={(result) => selectViewTarget(result)}
                                memberList={task.taskLabelSettingNew?.members || []}
                                teamList={task.taskLabelSettingNew?.teams || []}
                                defaultSelectScope="manager"
                                isRealTime
                                blockedMemberIdList={categoryViewMembers.map((v) => v.id)}
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "right",
                                marginTop: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                                onClick={() => setIsSettableMember((cur) => !cur)}
                                role="none"
                              >
                                {buildCheckBox(isSettableMember)}
                                プロジェクト編集権限対象を別に設定する
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    {
                      isSettableMember && (
                        <fieldset>
                          <legend>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <Tips
                                description="追加されたプロジェクト編集権限対象ユーザーは、このプロジェクトが編集できるようになります。"
                                className=""
                              >
                                <>
                                  プロジェクト編集権限対象
                                </>
                              </Tips>
                            </div>
                          </legend>
                          <div className="item w100">
                            <div className="detail equal_space row_1">
                              <div
                                className="team_area"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  gap: "16px",
                                }}
                              >
                                {
                                  !!selectSettableTeams.length && (
                                    <div
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "16px",
                                        position: "relative",
                                        backgroundColor: "#FFFFFF",
                                      }}
                                    >
                                      <div
                                        style={{
                                          backgroundColor: "#FFFFFF",
                                          padding: "0 4px",
                                          position: "absolute",
                                          top: 0,
                                          left: "8px",
                                          transform: "translateY(-50%)",
                                          fontSize: "10px",
                                          color: "#666666",
                                        }}
                                      >
                                        チーム
                                      </div>
                                      {
                                        selectSettableTeams.map((team) => (
                                          <div
                                            style={{
                                              border: "solid 1px #CCCCCC",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "8px",
                                                borderBottom: "solid 1px #CCCCCC",
                                                backgroundColor: "#EEEEEE",
                                              }}
                                            >
                                              <div>
                                                {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                              </div>
                                              <div>
                                                <span
                                                  className="delete"
                                                  onClick={() => changeSettableTeam([...selectSettableTeams.filter((v) => v.id !== team.id)])}
                                                  role="none"
                                                  style={{
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'times']} />
                                                </span>
                                              </div>
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "12px",
                                                padding: "12px 8px 8px",
                                              }}
                                            >
                                              {
                                                ["both", "manager"].includes(team.scope) && (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                      borderRadius: "4px",
                                                      padding: "8px",
                                                      position: "relative",
                                                      backgroundColor: "#FFFFFF",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        backgroundColor: "#FFFFFF",
                                                        padding: "0 4px",
                                                        position: "absolute",
                                                        top: 0,
                                                        left: "8px",
                                                        transform: "translateY(-50%)",
                                                        fontSize: "10px",
                                                        color: "#666666",
                                                      }}
                                                    >
                                                      マネージャー
                                                    </div>
                                                    <div
                                                      className="chip_area"
                                                      style={{
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {
                                                        buildTargetMember(team, "manager").map((member) => (
                                                          <div
                                                            className="chip"
                                                            key={`member_${member.id}`}
                                                          >
                                                            {member.name}
                                                          </div>
                                                        ))
                                                      }
                                                    </div>
                                                  </div>
                                                )
                                              }
                                              {
                                                ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                      borderRadius: "4px",
                                                      padding: "8px",
                                                      position: "relative",
                                                      backgroundColor: "#FFFFFF",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        backgroundColor: "#FFFFFF",
                                                        padding: "0 4px",
                                                        position: "absolute",
                                                        top: 0,
                                                        left: "8px",
                                                        transform: "translateY(-50%)",
                                                        fontSize: "10px",
                                                        color: "#666666",
                                                      }}
                                                    >
                                                      このチームのメンバー
                                                    </div>
                                                    <div
                                                      className="chip_area"
                                                      style={{
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {
                                                        buildTargetMember(team, "member").map((member) => (
                                                          <div
                                                            className="chip"
                                                            key={`member_${member.id}`}
                                                          >
                                                            {member.name}
                                                          </div>
                                                        ))
                                                      }
                                                    </div>
                                                  </div>
                                                )
                                              }
                                              {
                                                ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                      borderRadius: "4px",
                                                      padding: "8px",
                                                      position: "relative",
                                                      backgroundColor: "#FFFFFF",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        backgroundColor: "#FFFFFF",
                                                        padding: "0 4px",
                                                        position: "absolute",
                                                        top: 0,
                                                        left: "8px",
                                                        transform: "translateY(-50%)",
                                                        fontSize: "10px",
                                                        color: "#666666",
                                                      }}
                                                    >
                                                      このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                    </div>
                                                    <div
                                                      className="chip_area"
                                                      style={{
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {
                                                        Array.from(new Map([
                                                          ...getAllMemberList(team.id || null),
                                                          ...buildTargetMember(team, "member"),
                                                        ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                          <div
                                                            className="chip"
                                                            key={`member_${member.id}`}
                                                          >
                                                            {member.name}
                                                          </div>
                                                        ))
                                                      }
                                                    </div>
                                                  </div>
                                                )
                                              }
                                            </div>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )
                                }
                                {
                                  !!selectSettableMembers.length && (
                                    <div
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "16px",
                                        position: "relative",
                                        backgroundColor: "#FFFFFF",
                                      }}
                                    >
                                      <div
                                        style={{
                                          backgroundColor: "#FFFFFF",
                                          padding: "0 4px",
                                          position: "absolute",
                                          top: 0,
                                          left: "8px",
                                          transform: "translateY(-50%)",
                                          fontSize: "10px",
                                          color: "#666666",
                                        }}
                                      >
                                        ユーザー
                                      </div>
                                      <div className="chip_area">
                                        {
                                          selectSettableMembers.map((member) => (
                                            <div
                                              className="chip"
                                              key={`member_${member.id}`}
                                            >
                                              {member.name}
                                              <span
                                                className="delete"
                                                onClick={() => changeSettableMember([...selectSettableMembers.filter((v) => v.id !== member.id)])}
                                                role="none"
                                              >
                                                <FontAwesomeIcon icon={['fas', 'times']} />
                                              </span>
                                            </div>
                                          ))
                                        }
                                      </div>
                                    </div>
                                  )
                                }
                                <div className="chip_area">
                                  <AddTeamAndMemberModule
                                    selectMemberIdList={selectSettableMembers.map((v) => v.id)}
                                    selectTeamList={selectSettableTeams}
                                    callback={(result) => selectSettableTarget(result)}
                                    memberList={task.taskLabelSettingNew?.members || []}
                                    teamList={task.taskLabelSettingNew?.teams || []}
                                    defaultSelectScope="manager"
                                    isRealTime
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      )
                    }
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          プロジェクト業務仕分け対象
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div className="team_area" style={{ gap: "16px" }}>
                            {
                              !!selectUseTeams.length && (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    padding: "16px",
                                    position: "relative",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#FFFFFF",
                                      padding: "0 4px",
                                      position: "absolute",
                                      top: 0,
                                      left: "8px",
                                      transform: "translateY(-50%)",
                                      fontSize: "10px",
                                      color: "#666666",
                                    }}
                                  >
                                    チーム
                                  </div>
                                  {
                                    selectUseTeams.map((team) => (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px",
                                            borderBottom: "solid 1px #CCCCCC",
                                            backgroundColor: "#EEEEEE",
                                          }}
                                        >
                                          <div>
                                            {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                          </div>
                                          <div>
                                            <span
                                              className="delete"
                                              onClick={() => changeUseTeam([...selectUseTeams.filter((v) => v.id !== team.id)])}
                                              role="none"
                                              style={{
                                                cursor: "pointer",
                                              }}
                                            >
                                              <FontAwesomeIcon icon={['fas', 'times']} />
                                            </span>
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "12px",
                                            padding: "12px 8px 8px",
                                          }}
                                        >
                                          {
                                            ["both", "manager"].includes(team.scope) && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  マネージャー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    buildTargetMember(team, "manager").map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  このチームのメンバー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    buildTargetMember(team, "member").map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                </div>
                                                <div
                                                  className="chip_area"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    Array.from(new Map([
                                                      ...getAllMemberList(team.id || null),
                                                      ...buildTargetMember(team, "member"),
                                                    ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                      <div
                                                        className="chip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                              )
                            }
                            {
                              !!selectUseMembers.length && (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    padding: "16px",
                                    position: "relative",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#FFFFFF",
                                      padding: "0 4px",
                                      position: "absolute",
                                      top: 0,
                                      left: "8px",
                                      transform: "translateY(-50%)",
                                      fontSize: "10px",
                                      color: "#666666",
                                    }}
                                  >
                                    ユーザー
                                  </div>
                                  <div className="chip_area">
                                    {
                                      selectUseMembers.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                          <span
                                            className="delete"
                                            onClick={() => changeUseMember([...selectUseMembers.filter((v) => v.id !== member.id)])}
                                            role="none"
                                          >
                                            <FontAwesomeIcon icon={['fas', 'times']} />
                                          </span>
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              )
                            }
                            <div className="chip_area">
                              <AddTeamAndMemberModule
                                selectMemberIdList={selectUseMembers.map((v) => v.id)}
                                selectTeamList={selectUseTeams}
                                callback={(result) => selectUseTarget(result)}
                                memberList={task.taskLabelSettingNew?.members || []}
                                teamList={task.taskLabelSettingNew?.teams || []}
                                defaultSelectScope="member"
                                isRealTime
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          プロジェクト業務
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail row_1">
                          {
                            inputLabels.map((inputLabel, i) => (
                              <React.Fragment key={`job_${i.toString()}`}>
                                <div className="label_area">
                                  <SetLabelModule
                                    currentLabelName={inputLabel.label_name || null}
                                    callbackLabel={(labelName) => selectLabel(labelName, i)}
                                    labelList={tasks}
                                    existLabel={!!inputLabel.label_master_id}
                                  />
                                  <Tips
                                    description="業務を削除"
                                    className="del"
                                    icon={false}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => delLabelList(i)}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                    </button>
                                  </Tips>
                                </div>
                              </React.Fragment>
                            ))
                          }
                          {
                            labelsError && (
                              <div
                                style={{
                                  color: "#FF0000",
                                  margin: "4px 0",
                                }}
                              >
                                {labelsError}
                              </div>
                            )
                          }
                          <div className="add_area">
                            <button
                              type="button"
                              onClick={addLabelList}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              &nbsp;
                              業務追加
                            </button>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "8px",
                        gap: "8px",
                      }}
                    >
                      <button
                        type="button"
                        className="button cancel"
                        onClick={cancel}
                      >
                        キャンセル
                      </button>
                      <button
                        type="button"
                        className="button"
                        onClick={addLabelGroup}
                        disabled={!inputGroupName || isSubmit}
                      >
                        編集する
                      </button>
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <Prompt
        when={isEdit}
        message="このページから移動しますか？行った変更が保存されない可能性があります。"
      />
      <AddLabelGroupCategoryModal
        modalState={{ type: "create" }}
        closeCallback={() => setIsDisplayAddCategory(false)}
        addCallback={(param) => addProjectCategory(param)}
        modCallback={() => { /*  */ }}
        isDisplay={isDisplayAddCategory}
        categoryList={buildCategoryList()}
        memberList={task.taskLabelSettingNew?.members.map((v) => (
          { member_id: v.id, name: v.name, profile_image: v.profile_image, kana: v.kana }
        )) || []}
        labelGroupList={[]}
      />
    </section>
  )
}

export default Detail
